import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { BehaviorSettings } from '@/components/features/settings/behavior-settings';
import { ProfileSettings } from '@/components/features/settings/profile-settings';
import { MobileTabChips } from '@/components/shared/mobile-tab-chips';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { isNativePlatform } from '@/lib/native-features';
import { useState } from 'react';

function NativeSettings() {
  const { t } = useTranslation();
  const isNative = isNativePlatform();
  const [biometricEnabled, setBiometricEnabled] = useState(
    localStorage.getItem('biometric_auth_enabled') === 'true',
  );

  if (!isNative) return null;

  return (
    <Card className="md:border md:rounded-xl border-0 rounded-none shadow-none md:shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-lg">
            &#x1F91A;
          </div>
          <CardTitle className="text-sm font-semibold">
            {t('settings.tabs.mobile', { defaultValue: 'Mobile' })}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0 p-3 md:p-6">
        <div className="rounded-xl px-3.5 py-3 md:px-4 md:py-3.5 bg-muted/30 md:bg-transparent">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label
                htmlFor="biometric-auth"
                className="text-sm font-medium cursor-pointer leading-tight"
              >
                {t('settings.biometric.label', { defaultValue: 'Biometric Login' })}
              </Label>
              <p className="text-[11px] md:text-xs text-muted-foreground leading-relaxed mt-0.5">
                {t('settings.biometric.description', {
                  defaultValue: 'Require fingerprint or face verification when opening the app.',
                })}
              </p>
            </div>
            <Switch
              id="biometric-auth"
              checked={biometricEnabled}
              onCheckedChange={(checked) => {
                localStorage.setItem('biometric_auth_enabled', String(checked));
                setBiometricEnabled(checked);
              }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Settings() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('profile');

  if (isMobile) {
    return (
      <div className="space-y-4">
        <MobileTabChips
          tabs={[
            { value: 'profile', label: t('settings.tabs.profile') },
            { value: 'behavior', label: t('settings.tabs.behavior') },
          ]}
          value={tab}
          onChange={setTab}
        />
        {tab === 'profile' && (
          <div className="space-y-4">
            <ProfileSettings />
            <NativeSettings />
          </div>
        )}
        {tab === 'behavior' && <BehaviorSettings />}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('settings.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('settings.description')}</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profile">{t('settings.tabs.profile')}</TabsTrigger>
          <TabsTrigger value="behavior">{t('settings.tabs.behavior')}</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ProfileSettings />
          </div>
          <NativeSettings />
        </TabsContent>

        <TabsContent value="behavior" className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <BehaviorSettings />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/settings')({
  component: Settings,
});
